import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import laporanService from '../../services/laporan-service';

/* ─── Helpers ─────────────────────────────────────────────── */
const initialFilters = {
  tanggal_mulai: '',
  tanggal_akhir: '',
};

/* ─── Styles ──────────────────────────────────────────────── */
const thStyle = {
  padding: '12px 16px',
  textAlign: 'left',
  color: '#475569',
  fontWeight: 600,
  textTransform: 'uppercase',
  fontSize: '11px',
  letterSpacing: '0.05em',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '14px 16px',
  verticalAlign: 'middle',
  fontSize: '13px',
  color: '#334155',
};

/* ─── Main Component ──────────────────────────────────────── */
const LaporanPendaftaran = () => {
  const [filters, setFilters] = useState(initialFilters);
  const [laporan, setLaporan] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  const fetchLaporan = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = { ...filters };
      Object.keys(params).forEach((key) => {
        if (!params[key]) delete params[key];
      });

      const response = await laporanService.getRegistrationReport(params);
      setLaporan(response.data || []);
    } catch (err) {
      console.error(err);
      setError('Gagal mengambil laporan pendaftaran. Silakan coba lagi.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchLaporan();
  }, [fetchLaporan]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    setFilters(initialFilters);
  };

  const totalRegistrations = laporan.reduce((sum, item) => sum + Number(item.total || 0), 0);

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleExportPdf = async () => {
    setExporting(true);
    setError('');

    try {
      const params = { ...filters };
      Object.keys(params).forEach((key) => {
        if (!params[key]) delete params[key];
      });

      const fileData = await laporanService.exportRegistrationPdf(params);
      downloadBlob(fileData, 'laporan-pendaftaran.pdf');
    } catch (err) {
      console.error(err);
      setError('Gagal mengekspor laporan pendaftaran ke PDF.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <AdminLayout title="Laporan Pendaftaran">
      <div className="dashboard-section">
        
        {/* Header */}
        <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#0f172a' }}>Filter Laporan Pendaftaran</h2>
          <Link to="/admin/alur-kerja" className="view-all" style={{ fontSize: '14px', textDecoration: 'none', color: '#2563eb', fontWeight: 500 }}>
            Kembali ke dashboard laporan →
          </Link>
        </div>

        <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '24px' }}>
          Filter laporan pendaftaran berdasarkan rentang tanggal pendaftaran.
        </p>

        {/* Filter Form */}
        <div className="form" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', backgroundColor: '#fff', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
          
          <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label htmlFor="tanggal_mulai" style={{ fontSize: '13px', fontWeight: 500, color: '#334155' }}>Tanggal Mulai</label>
            <input
              id="tanggal_mulai"
              name="tanggal_mulai"
              type="date"
              value={filters.tanggal_mulai}
              onChange={handleChange}
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px' }}
            />
          </div>

          <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label htmlFor="tanggal_akhir" style={{ fontSize: '13px', fontWeight: 500, color: '#334155' }}>Tanggal Akhir</label>
            <input
              id="tanggal_akhir"
              name="tanggal_akhir"
              type="date"
              value={filters.tanggal_akhir}
              onChange={handleChange}
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px' }}
            />
          </div>

          {/* Action Buttons */}
          <div className="form-actions" style={{ gridColumn: '1 / -1', display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '8px', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
            <button type="button" className="btn" onClick={fetchLaporan} style={{ padding: '10px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#2563eb', color: '#fff', fontSize: '14px', fontWeight: 500, cursor: 'pointer' }}>
              Terapkan Filter
            </button>
            <button type="button" className="btn" onClick={handleExportPdf} disabled={exporting} style={{ padding: '10px 16px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: '#fff', color: '#334155', fontSize: '14px', fontWeight: 500, cursor: 'pointer' }}>
              {exporting ? 'Mengekspor PDF…' : 'Export PDF'}
            </button>
            <button type="button" className="btn danger" onClick={resetFilters} style={{ padding: '10px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#ef4444', color: '#fff', fontSize: '14px', fontWeight: 500, cursor: 'pointer', marginLeft: 'auto' }}>
              Reset Filter
            </button>
          </div>
        </div>

        {error && <p className="form-error" style={{ color: '#ef4444', fontSize: '14px', marginBottom: '16px' }}>{error}</p>}

        {/* Stats + Tabel */}
        <div className="dashboard-section" style={{ padding: '0', boxShadow: 'none', background: 'transparent' }}>
          
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
              <p>Memuat data laporan...</p>
            </div>
          ) : (
            <>
              {/* Stat Card */}
              <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                <div className="stat-card" style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '20px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', borderLeft: '4px solid #3b82f6' }}>
                  <div className="stat-icon" style={{ fontSize: '28px', backgroundColor: '#eff6ff', padding: '12px', borderRadius: '8px' }}>🗂️</div>
                  <div className="stat-info">
                    <h3 style={{ fontSize: '13px', color: '#64748b', fontWeight: 500, margin: 0 }}>Total Pendaftaran</h3>
                    <p className="stat-value" style={{ fontSize: '24px', fontWeight: 700, color: '#0f172a', margin: '4px 0 0 0' }}>{totalRegistrations.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* Tabel Hasil */}
              <div className="dashboard-section" style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#0f172a', margin: 0 }}>Hasil Laporan Pendaftaran</h3>
                  <span style={{ fontSize: '13px', color: '#64748b', backgroundColor: '#f1f5f9', padding: '4px 10px', borderRadius: '12px', fontWeight: 500 }}>
                    Status Rekapitulasi
                  </span>
                </div>

                <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <table className="table-report" style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ ...thStyle, width: '60px', textAlign: 'center' }}>No.</th>
                        <th style={thStyle}>Status Pendaftaran</th>
                        <th style={{ ...thStyle, width: '200px', textAlign: 'center' }}>Total Transaksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {laporan.length > 0 ? (
                        laporan.map((item, index) => (
                          <tr key={item.status || index} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ ...tdStyle, textAlign: 'center', color: '#64748b', fontWeight: 500 }}>
                              {index + 1}
                            </td>
                            <td style={tdStyle}>
                              <span style={{
                                textTransform: 'capitalize',
                                fontWeight: 600,
                                color: item.status === 'selesai' ? '#16a34a' : item.status === 'batal' ? '#ef4444' : '#1e293b'
                              }}>
                                {item.status || '-'}
                              </span>
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600, color: '#2563eb' }}>
                              <span style={{ backgroundColor: '#eff6ff', padding: '6px 16px', borderRadius: '12px', display: 'inline-block', minWidth: '40px' }}>
                                {item.total ?? 0}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3" style={{ textAlign: 'center', padding: '48px', color: '#94a3b8' }}>
                            <div style={{ fontSize: '24px', marginBottom: '8px' }}>🔍</div>
                            Tidak ada data laporan pendaftaran yang sesuai filter.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default LaporanPendaftaran;